import {
  dropRackDisplayCount,
  pickupRackDisplayCount,
  STOCKER_DROP_LP_RELEASE_PROGRESS,
  STOCKER_PICKUP_OHT_TAKE_PROGRESS,
} from '../../components/canvas/amhs/Stocker/stockerTransferTiming';
import {
  isDropLpReleasePending,
  isPickupLpTakePending,
} from './liveInventoryHold';
import type { Port } from '../../utils/ports';
import type {
  AmhsLiveInventory,
  AmhsLiveInventoryStocker,
  LiveOhtRuntimeFrame,
} from '../../types/amhsStream';
import {
  findLiveStocker,
  liveStockerColumnSplit,
  type LiveStockerColumnKind,
} from './liveStockerInventory';
import { resolveStockerTransferColumnKind } from './stockerTransferColumnKind';
import type { OhtTimelineEvent } from '../../types/amhsStream';
import {
  resetStocker2dDisplayInventoryTracking,
  resetStocker2dDisplayRuntimeTracking,
} from '../../utils/stocker2dDisplaySubscribe';

/** Per-column LP milestone overrides; omitted columns use held inventory. */
export type Stocker2DDisplayCounts = {
  raw?: number;
  processed?: number;
};

type ColumnTransferStart = {
  count: number;
  eventId: string;
};

const columnTransferStarts = new Map<string, ColumnTransferStart>();
const prevColumns = new Map<string, { raw: number; processed: number }>();
const displaySnapshotCache = new Map<string, Stocker2DDisplayCounts>();

function columnStateKey(stockerKey: string, kind: LiveStockerColumnKind): string {
  return `${stockerKey}:${kind}`;
}

function clearTransferState(stockerKey: string): void {
  columnTransferStarts.delete(columnStateKey(stockerKey, 'raw'));
  columnTransferStarts.delete(columnStateKey(stockerKey, 'processed'));
}

function stableDisplayCounts(
  stockerKey: string,
  next: Stocker2DDisplayCounts
): Stocker2DDisplayCounts {
  const cached = displaySnapshotCache.get(stockerKey);
  if (cached && cached.raw === next.raw && cached.processed === next.processed) {
    return cached;
  }
  displaySnapshotCache.set(stockerKey, next);
  return next;
}

/** Clear per-stocker transfer seeds when simulation stops or seeks. */
export function resetStocker2dDisplayState(): void {
  columnTransferStarts.clear();
  prevColumns.clear();
  displaySnapshotCache.clear();
  resetStocker2dDisplayRuntimeTracking();
  resetStocker2dDisplayInventoryTracking();
}

/** Held inventory lags/ahead of stream while a handoff is pinned — keep the held value. */
function resolveIdleColumnCount(displayCol: number, streamCol: number): number {
  if (streamCol > displayCol) return displayCol;
  if (streamCol < displayCol) return displayCol;
  return streamCol;
}

/**
 * Idle column after a transfer ends. Keeps LP-committed dot counts until held
 * inventory catches up — avoids 6 → 5 → 6 flash when stream leads display.
 */
export function resolveIdleAfterTransfer(
  displayCol: number,
  streamCol: number,
  prevCol: number
): number {
  const idle = resolveIdleColumnCount(displayCol, streamCol);
  if (streamCol > displayCol && prevCol > idle) return prevCol;
  if (streamCol < displayCol && prevCol < idle) return prevCol;
  return idle;
}

function buildStockerPortContext(
  elementId: string,
  aliases: readonly string[],
  layoutPorts: readonly Port[],
  stockerPortOwners: Record<string, string> | undefined,
  elements: Record<string, { customProperties?: Record<string, unknown>; metadata?: { name?: string } }>,
  inventory: AmhsLiveInventory | null
): {
  portIds: Set<string>;
  ownerIds: Set<string>;
} {
  const portIds = new Set<string>();
  for (const port of layoutPorts) {
    if (port.id) portIds.add(port.id);
  }
  if (stockerPortOwners && elementId) {
    for (const [portId, ownerId] of Object.entries(stockerPortOwners)) {
      if (ownerId === elementId) portIds.add(portId);
    }
  }

  const ownerIds = new Set<string>();
  if (elementId) ownerIds.add(elementId);
  const stockerEl = elementId ? elements[elementId] : undefined;
  const amhsId = stockerEl?.customProperties?.amhsId;
  const stockerId = stockerEl?.customProperties?.stockerId;
  const name = stockerEl?.metadata?.name;
  if (typeof amhsId === 'string' && amhsId.length > 0) ownerIds.add(amhsId);
  if (typeof stockerId === 'string' && stockerId.length > 0) ownerIds.add(stockerId);
  if (typeof name === 'string' && name.length > 0) ownerIds.add(name);
  for (const alias of aliases) {
    if (alias) ownerIds.add(alias);
  }

  const liveStocker = elementId ? findLiveStocker(inventory, elementId, aliases) : undefined;
  for (const port of liveStocker?.ports ?? []) {
    if (port.loadPortId) portIds.add(port.loadPortId);
  }
  if (liveStocker?.stockerId) ownerIds.add(liveStocker.stockerId);

  return { portIds, ownerIds };
}

function resolvePortColumnKind(
  layoutPorts: readonly Port[],
  portId: string | undefined,
  eventId: string | undefined,
  ohtTimelines: Record<string, OhtTimelineEvent[]> | undefined,
  liveStocker: AmhsLiveInventoryStocker | undefined
): LiveStockerColumnKind | undefined {
  const streamPortColumn = liveStocker?.ports?.find((port) => port.loadPortId === portId)?.column;
  return resolveStockerTransferColumnKind({
    layoutPorts,
    transferPortId: portId,
    eventId,
    streamColumn: streamPortColumn,
    ohtTimelines,
  });
}

export type ActiveStockerTransfer = {
  eventId: string;
  action: 'PICKUP' | 'DROP';
  progress01: number;
  portId: string;
  columnKind?: LiveStockerColumnKind;
};

const PORT_OWNER_SUFFIX = /^(.*)-LP-\d+$/i;

const canonicalStockerOwnerId = (value: string): string =>
  value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

function transferMatchesStocker(
  transfer: { portId: string; stationId?: string },
  portIds: ReadonlySet<string>,
  ownerIds: ReadonlySet<string>
): boolean {
  if (portIds.has(transfer.portId)) return true;
  if (transfer.stationId && ownerIds.has(transfer.stationId)) return true;
  const portOwnerId = PORT_OWNER_SUFFIX.exec(transfer.portId)?.[1];
  if (!portOwnerId) return false;
  if (ownerIds.has(portOwnerId)) return true;
  const canonicalOwner = canonicalStockerOwnerId(portOwnerId);
  for (const ownerId of ownerIds) {
    if (canonicalStockerOwnerId(ownerId) === canonicalOwner) return true;
  }
  return false;
}

/** Active OHT handoffs grouped by rack column (raw vs processed). */
export function findActiveStockerTransfersByColumn(
  frame: LiveOhtRuntimeFrame,
  portIds: ReadonlySet<string>,
  ownerIds: ReadonlySet<string>,
  layoutPorts: readonly Port[],
  ohtTimelines: Record<string, OhtTimelineEvent[]> | undefined,
  liveStocker: AmhsLiveInventoryStocker | undefined
): { raw: ActiveStockerTransfer | null; processed: ActiveStockerTransfer | null } {
  let bestRaw: ActiveStockerTransfer | null = null;
  let bestProcessed: ActiveStockerTransfer | null = null;

  for (const snapshot of Object.values(frame.byOhtId)) {
    const transfer = snapshot.transfer;
    if (!transfer || !transferMatchesStocker(transfer, portIds, ownerIds)) continue;

    const columnKind = resolvePortColumnKind(
      layoutPorts,
      transfer.portId,
      transfer.eventId,
      ohtTimelines,
      liveStocker
    );
    if (columnKind !== 'raw' && columnKind !== 'processed') continue;

    const candidate: ActiveStockerTransfer = {
      eventId: transfer.eventId,
      action: transfer.action,
      progress01: transfer.progress01,
      portId: transfer.portId,
      columnKind,
    };

    if (columnKind === 'raw') {
      if (!bestRaw || candidate.progress01 > bestRaw.progress01) bestRaw = candidate;
    } else if (!bestProcessed || candidate.progress01 > bestProcessed.progress01) {
      bestProcessed = candidate;
    }
  }

  return mergeTimelineStockerTransfers(
    frame,
    portIds,
    ownerIds,
    layoutPorts,
    ohtTimelines ?? {},
    liveStocker,
    { raw: bestRaw, processed: bestProcessed }
  );
}

/** Timeline handoffs before wall-hold transfer is visible on the runtime frame. */
function mergeTimelineStockerTransfers(
  frame: LiveOhtRuntimeFrame,
  portIds: ReadonlySet<string>,
  ownerIds: ReadonlySet<string>,
  layoutPorts: readonly Port[],
  ohtTimelines: Record<string, OhtTimelineEvent[]>,
  liveStocker: AmhsLiveInventoryStocker | undefined,
  current: { raw: ActiveStockerTransfer | null; processed: ActiveStockerTransfer | null }
): { raw: ActiveStockerTransfer | null; processed: ActiveStockerTransfer | null } {
  const globalS = frame.globalSimTimeS;
  let bestRaw = current.raw;
  let bestProcessed = current.processed;

  for (const events of Object.values(ohtTimelines)) {
    for (const event of events) {
      if (event.type !== 'transfer_event') continue;
      if (globalS + 1e-6 < event.startS) continue;
      if (
        !transferMatchesStocker(
          { portId: event.frame.portId, stationId: event.frame.stockerId },
          portIds,
          ownerIds
        )
      ) {
        continue;
      }

      const columnKind = resolvePortColumnKind(
        layoutPorts,
        event.frame.portId,
        event.eventId,
        ohtTimelines,
        liveStocker
      );
      if (columnKind !== 'raw' && columnKind !== 'processed') continue;

      const snapshot = frame.byOhtId[event.ohtId];
      const runtimeTransfer = snapshot?.transfer;
      const progress01 =
        runtimeTransfer && runtimeTransfer.eventId === event.eventId
          ? runtimeTransfer.progress01
          : 0;

      const pending =
        event.action === 'DROP'
          ? isDropLpReleasePending(
              {
                foupId: event.frame.foupId,
                ohtId: event.ohtId,
                portId: event.frame.portId,
                simTimeS: event.startS,
              },
              frame
            )
          : isPickupLpTakePending(
              {
                foupId: event.frame.foupId,
                ohtId: event.ohtId,
                portId: event.frame.portId,
                startS: event.startS,
              },
              frame
            );
      if (!pending) continue;

      const milestone =
        event.action === 'DROP'
          ? STOCKER_DROP_LP_RELEASE_PROGRESS
          : STOCKER_PICKUP_OHT_TAKE_PROGRESS;
      if (progress01 + 1e-6 >= milestone) continue;

      const candidate: ActiveStockerTransfer = {
        eventId: event.eventId,
        action: event.action,
        progress01,
        portId: event.frame.portId,
        columnKind,
      };

      if (columnKind === 'raw') {
        if (!bestRaw || candidate.progress01 > bestRaw.progress01) bestRaw = candidate;
      } else if (!bestProcessed || candidate.progress01 > bestProcessed.progress01) {
        bestProcessed = candidate;
      }
    }
  }

  return { raw: bestRaw, processed: bestProcessed };
}

/** Active OHT handoff at this stocker (armed hold only — same gate as 3D anim). */
export function findActiveStockerTransfer(
  frame: LiveOhtRuntimeFrame,
  portIds: ReadonlySet<string>,
  ownerIds: ReadonlySet<string>,
  layoutPorts: readonly Port[],
  ohtTimelines?: Record<string, OhtTimelineEvent[]>,
  liveStocker?: AmhsLiveInventoryStocker
): ActiveStockerTransfer | null {
  const byColumn = findActiveStockerTransfersByColumn(
    frame,
    portIds,
    ownerIds,
    layoutPorts,
    ohtTimelines ?? {},
    liveStocker
  );
  if (!byColumn.raw && !byColumn.processed) return null;
  if (!byColumn.raw) return byColumn.processed;
  if (!byColumn.processed) return byColumn.raw;
  return byColumn.raw.progress01 >= byColumn.processed.progress01
    ? byColumn.raw
    : byColumn.processed;
}

function applyColumnTransferDisplay(
  stockerKey: string,
  columnKind: LiveStockerColumnKind,
  active: ActiveStockerTransfer,
  prevCount: number,
  streamCount: number,
  capacity: number
): number {
  const stateKey = columnStateKey(stockerKey, columnKind);
  let start = columnTransferStarts.get(stateKey);
  if (!start || start.eventId !== active.eventId) {
    start = { count: prevCount, eventId: active.eventId };
    columnTransferStarts.set(stateKey, start);
  }

  if (active.action === 'DROP') {
    return dropRackDisplayCount(active.progress01, start.count, streamCount, capacity);
  }
  return pickupRackDisplayCount(active.progress01, start.count, streamCount, capacity);
}

/**
 * 2D rack occupancy driven by streamed transfer events + LP milestones (0.35
 * DROP / 0.75 PICKUP). Idle columns follow held inventory when it lags the
 * stream; active columns LP-adjust from the last rendered count so dots stay
 * aligned with Pick/Drop events.
 */
export function resolveStocker2DDisplayCounts(opts: {
  stockerKey: string;
  elementId: string;
  aliases: readonly string[];
  layoutPorts: readonly Port[];
  stockerPortOwners?: Record<string, string>;
  elements: Record<string, { customProperties?: Record<string, unknown>; metadata?: { name?: string } }>;
  /** Held inventory from liveInventoryDisplayStore (idle baseline). */
  inventory: AmhsLiveInventory | null;
  /** Unheld stream inventory — authoritative during active transfers. */
  rawInventory?: AmhsLiveInventory | null;
  runtimeFrame: LiveOhtRuntimeFrame;
  ohtTimelines?: Record<string, OhtTimelineEvent[]>;
  totalSlots: number;
}): Stocker2DDisplayCounts | undefined {
  const {
    stockerKey,
    elementId,
    aliases,
    layoutPorts,
    stockerPortOwners,
    elements,
    inventory,
    runtimeFrame,
    totalSlots,
  } = opts;
  const ohtTimelines = opts.ohtTimelines ?? {};
  const rawInventory = opts.rawInventory ?? inventory;

  const displayStocker = findLiveStocker(inventory, elementId, aliases);
  const rawStocker = findLiveStocker(rawInventory, elementId, aliases);
  const displayColumns = liveStockerColumnSplit(displayStocker);
  const streamColumns = liveStockerColumnSplit(rawStocker) ?? displayColumns;
  if (!displayStocker && !rawStocker && !displayColumns && !streamColumns) {
    return undefined;
  }

  const displayRaw = displayColumns?.raw ?? streamColumns?.raw ?? 0;
  const displayProcessed = displayColumns?.processed ?? streamColumns?.processed ?? 0;
  const streamRaw = streamColumns?.raw ?? displayRaw;
  const streamProcessed = streamColumns?.processed ?? displayProcessed;
  const capacity = Math.max(1, totalSlots);

  const { portIds, ownerIds } = buildStockerPortContext(
    elementId,
    aliases,
    layoutPorts,
    stockerPortOwners,
    elements,
    rawInventory
  );

  const activeByColumn = findActiveStockerTransfersByColumn(
    runtimeFrame,
    portIds,
    ownerIds,
    layoutPorts,
    ohtTimelines,
    rawStocker
  );

  const idleRawBaseline = resolveIdleColumnCount(displayRaw, streamRaw);
  const idleProcessedBaseline = resolveIdleColumnCount(displayProcessed, streamProcessed);
  const prev = prevColumns.get(stockerKey) ?? {
    raw: idleRawBaseline,
    processed: idleProcessedBaseline,
  };

  if (!activeByColumn.raw && !activeByColumn.processed) {
    clearTransferState(stockerKey);
    prevColumns.delete(stockerKey);
    displaySnapshotCache.delete(stockerKey);
    // Idle: canvas reads held inventory columns (Live Inventory panel source).
    // Returning counts here duplicated hold/stream merge and drifted processed.
    return undefined;
  }

  const next: Stocker2DDisplayCounts = {};
  if (activeByColumn.raw) {
    next.raw = Math.max(
      0,
      applyColumnTransferDisplay(
        stockerKey,
        'raw',
        activeByColumn.raw,
        prev.raw,
        streamRaw,
        capacity
      )
    );
  }
  if (activeByColumn.processed) {
    next.processed = Math.max(
      0,
      applyColumnTransferDisplay(
        stockerKey,
        'processed',
        activeByColumn.processed,
        prev.processed,
        streamProcessed,
        capacity
      )
    );
  }

  prevColumns.set(stockerKey, {
    raw: next.raw ?? idleRawBaseline,
    processed: next.processed ?? idleProcessedBaseline,
  });
  return stableDisplayCounts(stockerKey, next);
}

export function seedStocker2dDisplayState(
  stockerKey: string,
  start: { raw: number; processed: number; eventId: string },
  prev?: { raw: number; processed: number },
  columnKind: LiveStockerColumnKind = 'raw'
): void {
  columnTransferStarts.set(columnStateKey(stockerKey, columnKind), {
    count: columnKind === 'raw' ? start.raw : start.processed,
    eventId: start.eventId,
  });
  if (prev) prevColumns.set(stockerKey, prev);
}
